import moment from 'moment'

export type RevenueChartMetric = {
  bucket: string
  grossProduction: number
  netProduction: number
  collections?: number
}

export type BarData = { day: string; tooltipDisplay?: string; values: { [key: string]: number } }

export type BarsGroupedBy = `days` | `weeks` | `months`
export type ProductionType = `gross` | `net`

export const COLLECTIONS_LABEL = `Collections`

export function productionLabelFor(productionType: ProductionType): string {
  return productionType === `gross` ? `Gross Production` : `Net Production`
}

/**
 * Shapes daily metrics into chart bars for a single named series. Day grouping
 * is one bar per metric; week and month grouping sum into calendar intervals.
 * Reads only from the metrics it is given.
 */
function bucketSeries(
  metrics: RevenueChartMetric[],
  barsGroupedBy: BarsGroupedBy,
  getValue: (m: RevenueChartMetric) => number,
  label: string,
): BarData[] {
  if (barsGroupedBy === `days`) {
    return metrics.map((m) => ({
      day: moment(m.bucket).format(`MMM D YYYY`),
      tooltipDisplay: moment(m.bucket).format(`MMM D, YYYY`),
      values: { [label]: getValue(m) },
    }))
  }

  const groups = metrics.reduce(
    (acc, m) => {
      const interval =
        barsGroupedBy === `weeks`
          ? moment(m.bucket).startOf(`week`).format(`MM-DD-YY`)
          : moment(m.bucket).startOf(`month`).format(`MM-DD-YY`)

      if (acc[interval]) {
        acc[interval].value += getValue(m)
      } else {
        acc[interval] = { bucket: interval, value: getValue(m) }
      }
      return acc
    },
    {} as { [key: string]: { bucket: string; value: number } },
  )

  return Object.values(groups).map((g) => ({
    day:
      barsGroupedBy === `weeks`
        ? moment(g.bucket, `MM-DD-YY`).format(`MMM D YYYY`)
        : moment(g.bucket, `MM-DD-YY`).format(`MMM YYYY`),
    tooltipDisplay:
      barsGroupedBy === `weeks`
        ? `${moment(g.bucket, `MM-DD-YY`).format(`MMM D`)} - ${moment(g.bucket, `MM-DD-YY`)
            .endOf(`week`)
            .format(`MMM D, YYYY`)}`
        : moment(g.bucket, `MM-DD-YY`).format(`MMMM YYYY`),
    values: { [label]: g.value },
  }))
}

export function productionSeries(
  metrics: RevenueChartMetric[],
  barsGroupedBy: BarsGroupedBy,
  productionType: ProductionType,
): BarData[] {
  return bucketSeries(
    metrics,
    barsGroupedBy,
    (m) => (productionType === `gross` ? m.grossProduction : m.netProduction),
    productionLabelFor(productionType),
  )
}

/**
 * A bucket with no payments is a real data point, not a gap: it means nothing
 * came in that period. It stays in the series as zero so the collections bars
 * keep pairing one for one with the production bars.
 */
export function collectionsSeries(metrics: RevenueChartMetric[], barsGroupedBy: BarsGroupedBy): BarData[] {
  return bucketSeries(metrics, barsGroupedBy, (m) => m.collections ?? 0, COLLECTIONS_LABEL)
}
